import {
  AveragePlayerScore,
  AverageStat,
  BaseballAverageStatType,
  BasketballAverageStatType,
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import {
  PlayerSearchView,
  PlayerSearchViewAttributes,
} from '@sorare/core/src/lib/playerSearchView';

const orderedAverageScores: TotalAveragePlayerScore[] = [
  AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
  AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
  AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
  AveragePlayerScore.SEASON_AVERAGE_SCORE,
];

const orderedBreakdowns = [
  AveragePlayerScore.LAST_FIFTEEN_AVERAGE_ALL_AROUND_SCORE,
  AveragePlayerScore.LAST_FIFTEEN_AVERAGE_DECISIVE_SCORE,
];
const orderedProjections: So5FixtureEvent[] = [
  So5FixtureEvent.DAILY,
  So5FixtureEvent.CLASSIC,
];

export const nbaAverageStats: BasketballAverageStatType[] = [
  BasketballAverageStatType.POINTS,
  BasketballAverageStatType.REBOUNDS,
  BasketballAverageStatType.ASSISTS,
  BasketballAverageStatType.BLOCKS,
  BasketballAverageStatType.STEALS,
  BasketballAverageStatType.TURNOVERS,
  BasketballAverageStatType.THREE_POINTS_MADE,
  BasketballAverageStatType.DOUBLE_DOUBLE,
  BasketballAverageStatType.TRIPLE_DOUBLE,
  BasketballAverageStatType.MINS_PLAYED,
];

export const hittingBaseballAverageStats: BaseballAverageStatType[] = [
  BaseballAverageStatType.HITTING_RUNS,
  BaseballAverageStatType.HITTING_RUNS_BATTED_IN,
  BaseballAverageStatType.HITTING_SINGLES,
  BaseballAverageStatType.HITTING_DOUBLES,
  BaseballAverageStatType.HITTING_TRIPLES,
  BaseballAverageStatType.HITTING_HOME_RUNS,
  BaseballAverageStatType.HITTING_WALKS,
  BaseballAverageStatType.HITTING_STRIKEOUTS,
  BaseballAverageStatType.HITTING_STOLEN_BASES,
  BaseballAverageStatType.HITTING_HIT_BY_PITCH,
  BaseballAverageStatType.HITTING_CAUGHT_STEALING,
  BaseballAverageStatType.HITTING_AT_BASE,
  BaseballAverageStatType.HITTING_AT_PLATE,
];

export const pitchingBaseballAverageStats: BaseballAverageStatType[] = [
  BaseballAverageStatType.PITCHING_INNINGS_PITCHED,
  BaseballAverageStatType.PITCHING_STRIKEOUTS,
  BaseballAverageStatType.PITCHING_HITS_ALLOWED,
  BaseballAverageStatType.PITCHING_EARNED_RUNS,
  BaseballAverageStatType.PITCHING_WALKS,
  BaseballAverageStatType.PITCHING_HIT_BATSMEN,
  BaseballAverageStatType.PITCHING_WINS,
  BaseballAverageStatType.PITCHING_SAVES,
  BaseballAverageStatType.PITCHING_HOLDS,
  BaseballAverageStatType.PITCHING_NO_HITTERS,
  BaseballAverageStatType.PITCHING_RELIEF_APPEARANCE,
];

export const STATISTICS: AverageStat[] = [
  AverageStat.MINS_PLAYED,
  AverageStat.SET_PIECE_TAKEN,
];

export const DECISIVE_ACTIONS: AverageStat[] = [
  AverageStat.GOALS,
  AverageStat.GOAL_ASSIST,
  AverageStat.ASSIST_PENALTY_WON,
  AverageStat.LAST_MAN_TACKLE,
  AverageStat.CLEAN_SHEET,
  AverageStat.YELLOW_CARD,
  AverageStat.RED_CARD,
  AverageStat.ERROR_LEAD_TO_GOAL,
  AverageStat.PENALTY_CONCEDED,
  AverageStat.THREE_GOALS_CONCEDED,
];

const footballAverageStat = [...STATISTICS, ...DECISIVE_ACTIONS];
const baseballAvergaeStat = [
  ...hittingBaseballAverageStats,
  ...pitchingBaseballAverageStats,
];
const orderedAverageStatsBySport = {
  [Sport.NBA]: nbaAverageStats,
  [Sport.FOOTBALL]: footballAverageStat,
  [Sport.BASEBALL]: baseballAvergaeStat,
};

const reorder = <T extends string>(input: T[], order: T[]) =>
  order
    .map(stat => {
      const foundStat = input.find(s => s === stat);
      return foundStat || null;
    })
    .filter(Boolean);

export const reorderInput = (input: PlayerSearchView, sport: Sport) => {
  const stats = input[PlayerSearchViewAttributes.STATS];
  const reorderedStats = reorder<
    AverageStat | BasketballAverageStatType | BaseballAverageStatType
  >(stats, orderedAverageStatsBySport[sport]);

  const reorderedAveragePlayerScore = reorder(
    input[PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE],
    orderedAverageScores
  );
  const reorderedBreakdown = reorder(
    input[PlayerSearchViewAttributes.BREAKDOWN],
    orderedBreakdowns
  );
  const reorderedProjection = reorder(
    input[PlayerSearchViewAttributes.PROJECTION],
    orderedProjections
  );

  return {
    ...input,
    [PlayerSearchViewAttributes.STATS]: reorderedStats,
    [PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE]:
      reorderedAveragePlayerScore,
    [PlayerSearchViewAttributes.BREAKDOWN]: reorderedBreakdown,
    [PlayerSearchViewAttributes.PROJECTION]: reorderedProjection,
  };
};
